import React from 'react';
import { alpha, Box, useTheme } from '@mui/material';
import { NavLink, useLocation } from 'react-router';
import { IconButton } from '../../../Buttons/IconButton';
import { Icon, IconSize } from '../../../Icon';
import { Typography } from '../../../Typography';
import { NavbarLink, navButtonIsActive } from '../../utils';

interface FullNavBarLinkProps {
  fullNavButtons: Array<NavbarLink>;
  t: (key: string) => string;
}

/** Renders a the Full Nav Bar links */
const FullNavBarLink: React.FC<React.PropsWithChildren<FullNavBarLinkProps>> = ({
  fullNavButtons,
  t,
}: FullNavBarLinkProps) => {
  const theme = useTheme();
  const location = useLocation();
  return (
    <Box
      data-testid="desktop-full-nav-bar"
      sx={{
        ml: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
        gap: '2px',
      }}
    >
      {fullNavButtons.map((button) => {
        // Use matchPath if available, otherwise use href for matching
        const pathToMatch = button.matchPath || button.href;
        const isActive = navButtonIsActive(pathToMatch, location.pathname);
        return (
          <Box key={button.href} sx={{ display: 'flex' }}>
            <IconButton
              disableRipple
              sx={{
                borderRadius: '6px',
                backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
                p: '8px 12px',
                color: isActive ? alpha(theme.palette.primary.main, 0.8) : 'inherit',
                '&:focus-visible': {
                  boxShadow: `0px 0px 0px 2px #ffffff, 0px 0px 0px 4px ${theme.palette.primary.main}`,
                },
              }}
              aria-label={t(`${button.translation}`)}
              component={NavLink}
              to={button.href}
              end={button.href === '/app'}
              className={isActive ? 'active' : undefined}
            >
              <Icon
                size={IconSize.MD}
                folder={button.iconFolder}
                isStrokeColor={isActive}
                fillColor={isActive ? alpha(theme.palette.primary.main, 0.6) : 'inherit'}
              />
              <Typography
                variant="body1"
                ml={2}
                sx={{ color: isActive ? theme.palette.primary.main : '#414651', fontWeight: 600 }}
              >
                {t(`${button.translation}`)}
              </Typography>
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
};

export default FullNavBarLink;
